import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetServiceById, GetServiceItems, AddServiceItem, UpdateServiceItem, DeleteServiceItem,
  GetServicePayments, AddServicePayment, DeleteServicePayment, GetServiceItemTypes, UpdateService,
  AddCostLine, UpdateCostLine,
} from '../../api/api';
import { normaliseText, todayISO, EXPENSE_CATEGORIES } from '../../utils/helpers';
import { useEventCosts } from '../../utils/useEventCosts';
import { blankLine, lineMath, linesTotal, lineToForm, saveLines, parentMoney } from '../../utils/costLines';

export const PAYMENT_MODES = ['Cash', 'UPI', 'Bank', 'Cheque'];
export const OTHER_ITEM = 'Other';

const blankItem = () => ({ ItemTypeId: '', ItemName: '', ItemGroup: '', Quantity: '1', Rate: '', Remarks: '' });
const blankPayment = () => ({ PaymentDate: todayISO(), Amount: '', PaymentMode: 'Cash', Reference: '', Remarks: '' });

// One engagement in detail: its dynamic lines (menu dishes / jobs / songs — the
// same structure whatever the service is) and its payments. Shared by the
// desktop and mobile views; `vendorView` hides the money-editing controls.
// CHARGES: a service may be itemised into cost lines (utils/costLines.js) -
// Saffron's hall rent + a dinner for at least 700 that follows the Reception's
// headcount. Its total is then the live sum of the lines; saving the lines
// writes that total to the service (RateType 'Lines') so the API's balance and
// the vendor's My Work stay in step. The vendor never sees the lines (their
// guest list would be partial) - only the agreed total.
export function useServiceDetail() {
  const { serviceId } = useParams();
  const nav = useNavigate();
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  // What the vendor took off the quoted price (typed in the Charges dialog -
   // a discount is agreed on the whole quotation, never on one line).
  const [discountForm, setDiscountForm] = useState('');
  const [service, setService] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState(blankItem);
  const [payForm, setPayForm] = useState(blankPayment);
  const [savingItem, setSavingItem] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  // A vendor sees his own engagement read-only (except ticking jobs done).
  const isVendor = !!service && String(service.VendorUserId) === String(me) && !isAdmin;
  const canEdit = isAdmin || can('CanServicesEdit') || can('CanServices');
  const canMoney = canEdit && !isVendor;
  const costEvent = useMemo(() => (service?.EventId ? { EventId: service.EventId } : null), [service?.EventId]);
  const { functions, count, linesFor, fnName, reloadLines } = useEventCosts(costEvent, canMoney);
  const costLines = useMemo(() => (service ? linesFor('Service', service.ServiceId) : [])
    .map(l => ({ ...l, m: lineMath(l, count) })), [service, linesFor, count]);
  const itemised = costLines.length > 0;
  // A quotation carries three numbers, not one: what was quoted, what the
  // vendor took off, and what is owed - plus what it would come to with every
  // "if needed" line taken and every range at its ceiling.
  const liveMoney = parentMoney(service, itemised ? costLines : null, count, Number(service?.TotalAmount || 0));
  const liveTotal = liveMoney.total;
  const liveBalance = Math.max(0, liveTotal - Number(service?.AdvancePaid || 0));
  const [linesForm, setLinesForm] = useState(null);     // lines being edited, or null
  const [savingLines, setSavingLines] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      GetServiceById({ ServiceId: serviceId }),
      GetServiceItems({ ServiceId: serviceId }),
      GetServicePayments({ ServiceId: serviceId }),
    ]).then(([svc, its, pays]) => {
      const row = Array.isArray(svc) ? svc[0] : svc;
      setService(row || null);
      setItems(Array.isArray(its) ? its : []);
      setPayments(Array.isArray(pays) ? pays : []);
      if (row?.ServiceTypeId) {
        GetServiceItemTypes({ ServiceTypeId: row.ServiceTypeId })
          .then(t => setItemTypes(Array.isArray(t) ? t : [])).catch(() => setItemTypes([]));
      }
    }).catch(() => toast('Failed to load the service')).finally(() => setLoading(false));
  }, [serviceId, toast]);

  useEffect(() => { load(); }, [load]);

  // "Menu" for a caterer, "Job" for an event manager … comes from the master.
  const itemLabel = service?.ItemLabel || (service?.ServiceType === 'Catering' ? 'Menu' : 'Item');
  const itemOptions = useMemo(() => [
    ...itemTypes.map(t => ({ value: String(t.ItemTypeId), label: t.ItemGroup ? `${t.ItemTypeName} (${t.ItemGroup})` : t.ItemTypeName })),
    { value: OTHER_ITEM, label: 'Other (type a new one)' },
  ], [itemTypes]);

  // Lines grouped by their course / section, in master order.
  const itemsByGroup = useMemo(() => {
    const map = new Map();
    items.forEach(it => {
      const key = it.ItemGroup || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return Array.from(map.entries()).map(([group, list]) => ({ group, list }));
  }, [items]);

  const itemsTotal = useMemo(() => items.reduce((s, i) => s + Number(i.Amount || 0), 0), [items]);
  const paidTotal = useMemo(() => payments.reduce((s, p) => s + Number(p.Amount || 0), 0), [payments]);

  const setItemF = (k, v) => setItemForm(f => {
    const next = { ...f, [k]: ['ItemName', 'ItemGroup'].includes(k) ? normaliseText(v) : v };
    if (k === 'ItemTypeId' && v !== OTHER_ITEM) {
      const t = itemTypes.find(x => String(x.ItemTypeId) === String(v));
      next.ItemName = t?.ItemTypeName || '';
      next.ItemGroup = t?.ItemGroup || '';
    }
    if (k === 'ItemTypeId' && v === OTHER_ITEM) next.ItemName = '';
    return next;
  });
  const setPayF = (k, v) => setPayForm(f => ({ ...f, [k]: v }));

  const addItem = async () => {
    const name = itemForm.ItemTypeId === OTHER_ITEM ? String(itemForm.ItemName || '').trim() : itemForm.ItemName;
    if (!name) return toast(`Pick or type the ${itemLabel.toLowerCase()}`);
    setSavingItem(true);
    try {
      await AddServiceItem({
        ServiceId: service.ServiceId, EventId: service.EventId,
        ItemTypeId: itemForm.ItemTypeId === OTHER_ITEM ? null : (itemForm.ItemTypeId || null),
        ItemName: name, ItemGroup: itemForm.ItemGroup || null,
        Quantity: Number(itemForm.Quantity || 1), Rate: Number(itemForm.Rate || 0),
        Amount: Number(itemForm.Quantity || 1) * Number(itemForm.Rate || 0),
        Remarks: itemForm.Remarks || null, Status: 'Pending',
        Valid: 1, CreatedBy: me, UpdatedBy: me,
      });
      setItemForm(blankItem());
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Could not add');
    } finally { setSavingItem(false); }
  };

  // Jobs tick Done / Pending; menu lines can use it too (e.g. dish finalised).
  const toggleItemStatus = async (row) => {
    const Status = row.Status === 'Done' ? 'Pending' : 'Done';
    setItems(list => list.map(i => (i.ServiceItemId === row.ServiceItemId ? { ...i, Status } : i)));
    try {
      await UpdateServiceItem({ data: { Status, UpdatedBy: me }, where: `ServiceItemId = ${row.ServiceItemId}` });
    } catch { toast('Could not update'); load(); }
  };

  const removeItem = async (row) => {
    if (!window.confirm(`Remove ${row.ItemName}?`)) return;
    try {
      await DeleteServiceItem({ where: `ServiceItemId = ${row.ServiceItemId}` });
      load();
    } catch { toast('Could not remove'); }
  };

  const addPayment = async () => {
    if (!Number(payForm.Amount)) return toast('Enter the amount');
    setSavingPay(true);
    try {
      await AddServicePayment({
        ServiceId: service.ServiceId, EventId: service.EventId,
        PaymentDate: payForm.PaymentDate || todayISO(), Amount: Number(payForm.Amount),
        PaymentMode: payForm.PaymentMode || 'Cash', Reference: payForm.Reference || null,
        Remarks: payForm.Remarks || null, Valid: 1, CreatedBy: me, UpdatedBy: me,
      });
      setPayForm(blankPayment());
      setPayOpen(false);
      toast('Payment recorded');
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Could not save the payment');
    } finally { setSavingPay(false); }
  };

  const removePayment = async (row) => {
    if (!window.confirm(`Remove the payment of ${row.Amount}?`)) return;
    try {
      await DeleteServicePayment({ where: `PaymentId = ${row.PaymentId}` });
      load();
    } catch { toast('Could not remove'); }
  };

  // The vendor confirms the plate count he will cook for.
  const setConfirmedCount = async (count) => {
    try {
      await UpdateService({
        data: { ConfirmedCount: Number(count || 0), UpdatedBy: me },
        where: `ServiceId = ${service.ServiceId}`,
      });
      toast('Count updated');
      load();
    } catch { toast('Could not update the count'); }
  };

  // ── Charges (cost lines) ──
  const openLines = () => {
    setDiscountForm(Number(service?.Discount || 0) ? String(Number(service.Discount)) : '');
    return setLinesForm(costLines.length
    ? costLines.map(lineToForm)
    : [blankLine({ Title: service.ServiceType === 'Catering' ? 'Dinner' : (service.ServiceType || ''), FunctionId: service.FunctionId ? String(service.FunctionId) : '',
      Quantity: Number(service.ConfirmedCount) ? String(service.ConfirmedCount) : '', Rate: Number(service.Rate) ? String(Number(service.Rate)) : '',
      LiveSource: service.FunctionId && service.RateType === 'PerPlate' ? 'Headcount' : '' })]);
  };
  const closeLines = () => { if (!savingLines) setLinesForm(null); };
  const setDiscount = (v) => setDiscountForm(String(v).replace(/[^\d.]/g, ''));
  const setLine = (key, k, v) => setLinesForm(ls => ls.map(l => {
    if (l.key !== key) return l;
    let val = v;
    if (k === 'Title' || k === 'Category') val = normaliseText(v);
    if (['Quantity', 'Rate', 'Amount'].includes(k)) val = String(v).replace(/[^\d.]/g, '');
    if (k === 'Days') val = String(v).replace(/\D/g, '');
    const next = { ...l, [k]: val };
    if (k === 'LiveSource' && val && val !== 'Cards' && !l.FunctionId && service.FunctionId) next.FunctionId = String(service.FunctionId);
    return next;
  }));
  const addLine = (over = {}) => setLinesForm(ls => {
    const last = ls[ls.length - 1];
    const carry = last ? { FunctionId: last.FunctionId, FromDate: last.FromDate, ToDate: last.ToDate } : {};
    return [...ls, blankLine({ ...carry, ...over })];
  });
  const removeLine = (key) => setLinesForm(ls => ls.filter(l => l.key !== key));
  const lineInfo = (l) => lineMath(l, count);
  const saveCostLines = async () => {
    const named = linesForm.filter(l => String(l.Title || '').trim());
    const bad = named.find(l => l.Basis !== 'Fixed' && l.LiveSource && l.LiveSource !== 'Cards' && !l.FunctionId);
    if (bad) return toast(`"${bad.Title}": which function's count?`);
    setSavingLines(true);
    try {
      await saveLines({
        api: { add: AddCostLine, update: UpdateCostLine }, lines: named, saved: linesFor('Service', service.ServiceId),
        parentType: 'Service', parentId: service.ServiceId, eventId: service.EventId, count, me,
      });
      // The service's agreed total follows its lines; with none left it is a
      // plain fixed amount again (the last total kept).
      await UpdateService({
        // TotalAmount stays the lines BEFORE the discount - the QUOTED price -
        // so every screen can show quoted · discount · agreed.
        data: named.length
          ? { RateType: 'Lines', TotalAmount: linesTotal(named, count), Discount: Number(discountForm || 0) || 0, UpdatedBy: me }
          : { RateType: 'Fixed', Discount: Number(discountForm || 0) || 0, UpdatedBy: me },
        where: `ServiceId = ${service.ServiceId}`,
      });
      toast('Charges saved');
      setLinesForm(null);
      await reloadLines();
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Could not save the charges');
    } finally { setSavingLines(false); }
  };
  const setDueDate = async (DueDate) => {
    try {
      await UpdateService({ data: { DueDate: DueDate || null, UpdatedBy: me }, where: `ServiceId = ${service.ServiceId}` });
      setService(sv => ({ ...sv, DueDate }));
    } catch { toast('Could not save the due date'); }
  };

  const back = () => nav(-1);

  return {
    service, items, itemsByGroup, payments, loading, itemLabel, itemOptions, itemTypes,
    itemsTotal, paidTotal, isVendor, canEdit, canMoney,
    itemForm, setItemF, addItem, savingItem, toggleItemStatus, removeItem,
    payForm, setPayF, addPayment, savingPay, removePayment, payOpen, setPayOpen,
    setConfirmedCount, back,
    functions, fnName, costLines, itemised, liveTotal, liveBalance, categories: EXPENSE_CATEGORIES,
    linesForm, openLines, closeLines, setLine, addLine, removeLine, lineInfo, saveCostLines, savingLines, setDueDate,
    liveMoney, discountForm, setDiscount,
  };
}
